import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Anchor,
  Box,
  Card,
  CardBody,
  Grid,
  Heading,
  Page,
  PageContent,
  Text,
} from 'grommet';
import { Calendar, Group, LineChart, Trophy } from 'grommet-icons';

import { getSeasonConfig } from '../seasons';
import { findDoc } from '../docs/manifest';

const countdownTo = (date) => {
  const ms = new Date(date) - Date.now();
  if (ms <= 0) return null;
  const minutes = Math.floor(ms / 60000);
  return {
    days: Math.floor(minutes / (60 * 24)),
    hours: Math.floor(minutes / 60) % 24,
    minutes: minutes % 60,
  };
};

const CountdownTile = ({ value, unit }) => (
  <Box background="white" round="xsmall" pad="small" align="center">
    <Text size="34px" weight="bold" color="dark-1">{value}</Text>
    <Text size="xsmall" color="dark-2">{unit}</Text>
  </Box>
);

const LinkCard = ({ to, icon, title, blurb }) => (
  <Anchor as={Link} to={to} color="dark-1" style={{ textDecoration: 'none' }}>
    <Card background="light-2" elevation="none" fill>
      <CardBody pad="medium" gap="xsmall">
        {icon}
        <Text weight="bold" color="dark-1">{title}</Text>
        <Text size="small" color="dark-2">{blurb}</Text>
      </CardBody>
    </Card>
  </Anchor>
);

export default function PreseasonHome({ config }) {
  const [countdown, setCountdown] = useState(() => countdownTo(config.draftDate));

  useEffect(() => {
    const tick = setInterval(() => setCountdown(countdownTo(config.draftDate)), 60000);
    return () => clearInterval(tick);
  }, [config.draftDate]);

  const lastSeason = getSeasonConfig(config.season - 1);
  const lastSeasonDoc = lastSeason && findDoc(`${lastSeason.season}-season`);
  const champion = lastSeason?.results?.champion;

  return (
    <Page kind="narrow" pad="medium">
      <PageContent gap="medium">
        <Card background="light-2" border={{ color: 'brand', size: 'small' }} flex={false}>
          <CardBody pad="medium" gap="small">
            <Text size="xsmall" weight="bold" color="brand" style={{ letterSpacing: '2px' }}>
              {config.label.toUpperCase()} · {config.season}
            </Text>
            <Heading level={1} margin="none">
              {countdown ? 'Draft Day is coming' : 'Draft day has arrived'}
            </Heading>
            {countdown && (
              <Grid columns={{ count: 3, size: 'auto' }} gap="small">
                <CountdownTile value={countdown.days} unit="DAYS" />
                <CountdownTile value={countdown.hours} unit="HOURS" />
                <CountdownTile value={countdown.minutes} unit="MINUTES" />
              </Grid>
            )}
            <Text>
              {new Date(config.draftDate).toLocaleString('en-US', {
                weekday: 'long', month: 'long', day: 'numeric',
                hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York',
              })} ET · Live on ESPN
            </Text>
          </CardBody>
        </Card>

        <Card flex={false}>
          <CardBody pad="medium" gap="small">
            <Heading level={3} margin="none">Key dates</Heading>
            <Box gap="small">
              {config.keyDates.map((item) => (
                <Box key={item.label} direction="row" align="center" gap="small">
                  <Calendar size="18px" color={item.critical ? 'status-critical' : 'brand'} />
                  <Text weight="bold" color="dark-1" style={{ minWidth: '64px' }}>{item.date}</Text>
                  <Text color={item.critical ? 'status-critical' : undefined}
                        weight={item.critical ? 'bold' : undefined}>
                    {item.label}
                  </Text>
                </Box>
              ))}
            </Box>
          </CardBody>
        </Card>

        {lastSeason && (
          <Card flex={false}>
            <CardBody pad="medium" gap="small">
              <Heading level={3} margin="none">Last season — {lastSeason.season}</Heading>
              <Box direction="row" align="center" gap="small">
                <Trophy size="28px" color="status-warning" />
                <Box>
                  <Text weight="bold" color="dark-1">{champion ? champion.team : 'TBD'}</Text>
                  <Text size="small" color="dark-2">{lastSeason.season} Champion</Text>
                </Box>
              </Box>
              {lastSeasonDoc && (
                <Anchor as={Link} to={`/docs/${lastSeasonDoc.slug}`} weight="bold">
                  Full {lastSeason.season} archive →
                </Anchor>
              )}
            </CardBody>
          </Card>
        )}

        <Grid columns={{ count: 2, size: 'auto' }} gap="small">
          <LinkCard
            to="/history"
            icon={<LineChart size="20px" color="brand" />}
            title="League history"
            blurb="Champions, records, hall of shame"
          />
          <LinkCard
            to="/pals"
            icon={<Group size="20px" color="brand" />}
            title="Pals' leagues"
            blurb="Follow your pals' other teams"
          />
        </Grid>
      </PageContent>
    </Page>
  );
}
